import { connect as withFormik } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { FormattedMessage, useIntl } from 'react-intl'

import Button from '@/components/Button'
import CopyTextButton from '@/components/CopyTextButton'
import { Checkbox, Text } from '@/components/Form'
import KeyValue from '@/components/KeyValue'

import messages from './messages'
import { appFormPropTypes } from './propTypes'

import 'react-toggle/style.css'

import { isEmpty } from 'lodash'
import Toggle from 'react-toggle'

import DestructiveConfirmationOverlay from '@/components/DestructiveConfirmationOverlay'
import { ImageLibraryWithFormik } from '@/components/ImageLibrary'

const ConnectorFormFields = ({
  formik,
  registerOnCancelSmartNotification,
  currentConnector,
  onNavigateConnectorLogs
}: any) => {
  const intl = useIntl()

  const [isSmartNotificationsExpanded, setSmartNotificationsExpanded] = useState(false)
  const [isSmartNotificationsInfoSameAsApp, setSmartNotificationsInfoSameAsApp] = useState(false)
  const [showWarningBeforeDisablingSmartNotifications, setShowWarningBeforeDisablingSmartNotifications] =
    useState(false)

  useEffect(() => {
    const isSmartNotificationsExpanded = !isEmpty(formik.values.currentConnector.id)
    setSmartNotificationsExpanded(isSmartNotificationsExpanded)
    // need to wrap in setTimeout to apply formik setFieldValue in react CDM https://github.com/jaredpalmer/formik/issues/930
    setTimeout(() => formik.setFieldValue('isSmartNotificationsExpanded', isSmartNotificationsExpanded), 1)

    const isNotificationInfoDefaulted =
      formik.values.currentConnector.name === formik.values.currentApp.name &&
      formik.values.currentConnector.iconUrl === formik.values.currentApp.iconUrl
    setSmartNotificationsInfoSameAsApp(isNotificationInfoDefaulted)
    if (isNotificationInfoDefaulted) {
      setTimeout(() => formik.setFieldValue('currentConnector.defaultsSelected', ['defaults-selected']), 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.currentConnector?.id])

  useEffect(() => {
    const isCurrentConnectorEmpty = formik.errors.currentConnector
    if (isEmpty(isCurrentConnectorEmpty)) {
      formik.setFieldError('currentConnector', undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.errors])

  useEffect(() => {
    if (isSmartNotificationsInfoSameAsApp) {
      const { name, iconUrl } = formik.values.currentApp
      setTimeout(() => formik.setFieldValue('currentConnector.name', name), 1)
      setTimeout(() => formik.setFieldValue('currentConnector.iconUrl', iconUrl), 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.currentApp])

  const handleSmartNotificationToggleChange = (event: any) => {
    const toggleWasExpanded = event.target.value === 'true'
    const currentConnectorId = formik.values.currentConnector.id
    if (toggleWasExpanded && currentConnectorId) {
      setShowWarningBeforeDisablingSmartNotifications(true)
    } else {
      setSmartNotificationsState(!toggleWasExpanded)
    }
  }

  const setSmartNotificationsState = (isExpanded: any) => {
    setSmartNotificationsExpanded(isExpanded)
    formik.setFieldValue('isSmartNotificationsExpanded', isExpanded)
    !isExpanded && setShowWarningBeforeDisablingSmartNotifications(false)
  }

  const toggleDefaults = () => {
    const isDefault = !isSmartNotificationsInfoSameAsApp
    setSmartNotificationsInfoSameAsApp(isDefault)

    if (isDefault) {
      const { name, iconUrl } = formik.values.currentApp
      formik.setFieldValue('currentConnector.name', name)
      formik.setFieldValue('currentConnector.iconUrl', iconUrl)
    } else {
      const { name, iconUrl } = formik.values.currentConnector
      formik.setFieldValue('currentConnector.name', name)
      formik.setFieldValue('currentConnector.iconUrl', iconUrl)
    }
  }

  return (
    <div className="app-form-fields">
      <div className="additional-fields__label">
        <div className="rowApp">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>
            <h2>
              <FormattedMessage {...messages.settingsFunctionality} />
            </h2>
          </label>
        </div>
        <div className="additional-fields__row accordion-title">
          <FormattedMessage {...messages.smartNotifications} />
          <Toggle
            checked={isSmartNotificationsExpanded}
            name="isSmartNotificationsExpanded"
            value={String(isSmartNotificationsExpanded)}
            onChange={handleSmartNotificationToggleChange}
          />
          {!!showWarningBeforeDisablingSmartNotifications && (
            <DestructiveConfirmationOverlay
              onConfirm={() => setSmartNotificationsState(false)}
              onCancel={() => setShowWarningBeforeDisablingSmartNotifications(false)}
              onConfirmActionTitle={<FormattedMessage {...messages.disableConfirm} />}
              message={<FormattedMessage {...messages.disableCheck} />}
              subMessage={<FormattedMessage {...messages.disablingSmartNotifications} />}
              confirmIsPrimary={true}
            />
          )}
        </div>
        <div className="additional-fields-summary__label columnHeader">
          {!isSmartNotificationsExpanded && (
            <>
              <span className="subcomponent-text">
                <FormattedMessage {...messages.smartNotificationsDescription} />
              </span>
              <span>
                <FormattedMessage {...messages.subcomponentDescriptionOff} />
              </span>
            </>
          )}
        </div>
      </div>
      <AnimateHeight duration={500} height={isSmartNotificationsExpanded ? 'auto' : 0} animateOpacity>
        <div className="rowApp">
          <div className="columnHeader">
            <div className="name-icon-logs">
              <div className="custom-name-and-icon">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label className="field__label">
                  <FormattedMessage {...messages.sourceNameAndIcon} />
                </label>
                <Checkbox name="currentConnector.defaultsSelected" className="default-checked">
                  <Checkbox.Input
                    className="connector-default-name-and-url"
                    id="connector-default-name-and-url"
                    data-testid="connector-default-name-and-url"
                    key="connector-default-name-and-url"
                    label={<FormattedMessage {...messages.defaultNameAndUrl} />}
                    name="currentConnector.defaultsSelected"
                    value="defaults-selected"
                    onChange={toggleDefaults}
                  />
                </Checkbox>
              </div>
              {!isEmpty(currentConnector) && (
                <Button
                  color="tertiary"
                  className="view-logs-button form__field field__label"
                  onClick={onNavigateConnectorLogs}
                >
                  <FormattedMessage {...messages.logs} />
                </Button>
              )}
            </div>
            <div className="child-fields">
              <AnimateHeight duration={50} height={isSmartNotificationsInfoSameAsApp ? 0 : 'auto'} animateOpacity>
                <Text
                  label={<FormattedMessage {...messages.connectorName} />}
                  name="currentConnector.name"
                  placeholder={intl.formatMessage(messages.connectorNamePlaceholder)}
                  required
                />
                <div className="app-edit-icon-block app-icon-edit">
                  {/* @ts-expect-error */}
                  <ImageLibraryWithFormik
                    required
                    shouldValidate={isSmartNotificationsExpanded}
                    label={intl.formatMessage(messages.connectorIconUrl)}
                    name="currentConnector.iconUrl"
                    placeholder={intl.formatMessage(messages.connectorIconUrlPlaceholder)}
                    cancel={registerOnCancelSmartNotification}
                  />
                </div>
              </AnimateHeight>
            </div>
            <Text
              label={<FormattedMessage {...messages.webHookUrl} />}
              name="currentConnector.webHookUrl"
              placeholder={intl.formatMessage(messages.webHookUrlPlaceHolder)}
              type="url"
            />
            {formik.values.currentConnector.clientCredential && (
              <div className="sensitive-wrapper">
                {formik.values.currentConnector.clientCredential.clientId && (
                  <KeyValue>
                    <KeyValue.Label>
                      <FormattedMessage {...messages.clientId} />
                    </KeyValue.Label>
                    <KeyValue.Value>{formik.values.currentConnector.clientCredential.clientId}</KeyValue.Value>
                  </KeyValue>
                )}
                {formik.values.currentConnector.clientCredential.clientSecret && (
                  <KeyValue>
                    <KeyValue.Label>
                      <FormattedMessage {...messages.clientSecret} />{' '}
                      <span className="warning">
                        <FormattedMessage {...messages.onlyDisplayedOnce} />
                      </span>
                    </KeyValue.Label>
                    <KeyValue.Value>{formik.values.currentConnector.clientCredential.clientSecret} </KeyValue.Value>
                    <CopyTextButton text={formik.values.currentConnector.clientCredential.clientSecret} />
                  </KeyValue>
                )}
              </div>
            )}
          </div>
        </div>
      </AnimateHeight>
    </div>
  )
}

ConnectorFormFields.displayName = 'ConnectorFormFields'

ConnectorFormFields.propTypes = {
  ...appFormPropTypes,
  onNavigateConnectorLogs: PropTypes.func.isRequired,
  registerOnCancelSmartNotification: PropTypes.func
}

ConnectorFormFields.defaultProps = {
  actionMessage: null,
  currentConnector: {}
}

export default withFormik(ConnectorFormFields)
